import { useEffect, useRef, useState } from 'react'
import { AddOnsContext, SNAPSHOT_EVALUATOR } from './addons'
import { DeskProvider, useDesks } from './desks'
import { type JobProduct, api, type Principal } from './api'
import { BrandLogo, Wordmark } from './components/Brand'
import { HelpBook, HelpPanel } from './components/Help'
import { useMenuHint } from './components/MenuHint'
import { PendingActions } from './components/PendingActions'
import { Approvals } from './pages/Approvals'
import { Attest } from './pages/Attest'
import { Audit } from './pages/Audit'
import { AttestHistory } from './pages/AttestHistory'
import { ClauseReview } from './pages/ClauseReview'
import { Compare } from './pages/Compare'
import { CureObligations } from './pages/CureObligations'
import { SettlementCalendars } from './pages/SettlementCalendars'
import { Dictionary } from './pages/Dictionary'
import { Documents } from './pages/Documents'
import { Export } from './pages/Export'
import { Funds } from './pages/Funds'
import { Desks } from './pages/Desks'
import { Groups } from './pages/Groups'
import { Holdings } from './pages/Holdings'
import { HoldingsFeed } from './pages/HoldingsFeed'
import { SettingsNav, type SettingsGroup } from './components/SettingsNav'
import { JobHistory } from './pages/JobHistory'
import { Verdicts } from './pages/Verdicts'
import { WhatIf } from './pages/WhatIf'
import { Reconciliation } from './pages/Reconciliation'
import { Agreement } from './pages/Agreement'
import { Benchmarks } from './pages/Benchmarks'
import { ExcelAddin } from './pages/ExcelAddin'
import { Lookup } from './pages/Lookup'
import { MailboxSettings } from './pages/MailboxSettings'
import { Membership } from './pages/Membership'
import { Migration } from './pages/Migration'
import { Operations, type SystemSection } from './pages/Operations'
import { Promotions } from './pages/Promotions'
import { Redeem } from './pages/Redeem'
import { Reviews } from './pages/Reviews'
import { Search } from './pages/Search'
import { SignIn } from './pages/SignIn'
import { Submissions } from './pages/Submissions'
import { Users } from './pages/Users'

type Tab =
  | 'lookup'
  | 'compare'
  | 'search'
  | 'migration'
  | 'clauses'
  | 'documents'
  | 'approvals'
  | 'reviews'
  | 'reviews-all'
  | 'attest'
  | 'attest-history'
  | 'dictionary'
  | 'dictionary-requests'
  | 'dictionary-starter'
  | 'membership'
  | 'promotions'
  | 'submissions'
  | 'submissions-shapes'
  | 'submissions-senders'
  | 'connector'
  | 'audit'
  | 'users'
  | 'users-add'
  | 'operations'
  | 'export'
  | 'export-verify'
  | 'export-pack'
  | 'funds'
  | 'funds-add'
  | 'groups'
  | 'desks'
  | 'holdings'
  | 'holdings-load'
  | 'holdings-feed'
  | 'job-history'
  | 'verdicts'
  | 'agreement'
  | 'dictionary-reconcile'
  | 'whatif'
  | 'cure'
  | 'calendars'
  | 'benchmarks'
  | 'excel'

/**
 * Tabs are hidden by capability, and that is a courtesy rather than the control.
 * Every route re-checks server-side (§3.1); this only stops the UI offering
 * buttons that always fail, which is how users learn to ignore errors.
 */
interface Screen {
  id: Tab
  label: string
  /** One line, in the words somebody would use to ask for the screen. */
  blurb: string
  needs?: string
  /** Any one of these is enough. Seeding a market is the case: an approver may
   *  do it because it is vocabulary, and an administrator may because the
   *  console command it wraps needs no capability at all. */
  needsAny?: string[]
  /** An add-on pack the installation has to run. A screen from a pack it does not
   *  run is not offered at all, whatever the capabilities (decision 26). */
  pack?: string
}

/** Screens that belong together and to nobody else. Opens to the right. */
interface Group {
  title: string
  blurb: string
  screens: Screen[]
}

interface Section {
  title: string
  items: (Screen | Group)[]
}

const isGroup = (item: Screen | Group): item is Group => 'screens' in item

const SECTIONS: Section[] = [
  {
    // Where every fund stands, first on the bar and the home page where the Snapshot
    // Evaluator pack runs. Absent from the bar where it does not, like Evaluation.
    title: 'Dashboards',
    items: [
      {
        id: 'verdicts',
        label: 'Verdicts',
        blurb: 'Where every fund stands on its latest run',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'cure',
        label: 'Cure obligations',
        blurb: 'Downgraded holdings and the time left to sell them',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'agreement',
        label: 'Compliance agreement',
        blurb: 'Verdicts compliance reviewed, agreed and overrode',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
    ],
  },
  {
    title: 'Guidelines',
    items: [
      { id: 'lookup', label: 'Lookup', blurb: 'Everything that applies to one fund' },
      { id: 'compare', label: 'Compare', blurb: 'Two funds side by side' },
      { id: 'search', label: 'Search', blurb: 'Find a limit anywhere in the firm' },
      { id: 'excel', label: 'Excel add-in', blurb: 'Look up a fund from a cell in Excel' },
      {
        title: 'Export',
        blurb: 'Files to take out of this system',
        screens: [
          {
            id: 'export',
            label: 'Export limits',
            blurb: 'The rule file for your compliance system',
            needs: 'rule.export',
          },
          {
            id: 'export-verify',
            label: 'Check a file',
            blurb: 'Confirm a file you exported earlier is unaltered',
            needs: 'rule.export',
          },
          {
            id: 'export-pack',
            label: 'Audit pack',
            blurb: 'Limits, sign-offs and changes, for an auditor',
            needs: 'audit.pack.export',
          },
        ],
      },
    ],
  },
  {
    // The Snapshot Evaluator pack. Absent from the bar on an installation that does
    // not run it, since every screen here needs the pack.
    title: 'Evaluation',
    // In the order the work runs: holdings in, what they hold, what they came to, what follows.
    items: [
      {
        id: 'holdings-load',
        label: 'Load a snapshot',
        blurb: 'A holdings file from your OMS or custodian',
        needs: 'holdings.load',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'holdings',
        label: 'Holdings snapshots',
        blurb: 'The holdings verdicts are judged against',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'whatif',
        label: 'What-if trades',
        blurb: 'Candidate trades against a fund’s newest holdings',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        // Here rather than under Settings: compliance sets when deadlines fall, and it is
        // the approver's, who has nothing else under Settings.
        id: 'calendars',
        label: 'Settlement calendars',
        blurb: 'The days deadlines count on, and when to escalate',
        needs: 'cure.configure',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'benchmarks',
        label: 'Benchmarks',
        blurb: 'The figures limits against a benchmark are judged on',
        needs: 'evaluation.run',
        pack: SNAPSHOT_EVALUATOR,
      },
    ],
  },
  {
    title: 'Intake',
    items: [
      {
        id: 'migration',
        label: 'Manually upload guidelines',
        blurb: 'Load an existing guideline spreadsheet',
        needs: 'rule.draft',
      },
      {
        id: 'documents',
        label: 'Mandate documents',
        blurb: 'The agreements and letters your limits come from',
        needs: 'dictionary.browse',
      },
      {
        title: 'Guideline mailbox',
        blurb: 'What clients have sent in',
        screens: [
          {
            id: 'submissions',
            label: 'Refused items',
            blurb: 'Instructions that were not acted on, and why',
          },
          {
            id: 'submissions-shapes',
            label: 'Unmapped layouts',
            blurb: 'A document shape nobody has explained yet',
          },
          {
            id: 'submissions-senders',
            label: 'Who may send',
            blurb: 'Addresses allowed to submit, per fund',
          },
        ],
      },
    ],
  },
  {
    title: 'Approvals',
    items: [
      {
        id: 'clauses',
        label: 'Clause review',
        blurb: 'Match each limit to what the document says',
        needs: 'rule.draft',
      },
      { id: 'approvals', label: 'Awaiting approval', blurb: 'Proposed changes to decide on' },
      {
        title: 'Due for review',
        blurb: 'Limits to re-confirm, and documents that changed',
        screens: [
          {
            id: 'reviews',
            label: 'Mine',
            blurb: 'Limits you own that are due',
            needs: 'rule.review.confirm',
          },
          {
            id: 'reviews-all',
            label: 'Everyone',
            blurb: 'The firm’s whole review backlog',
            needs: 'rule.review.confirm',
          },
        ],
      },
      {
        title: 'Sign-off',
        blurb: 'Stating that a fund’s limits are complete and correct',
        screens: [
          {
            id: 'attest',
            label: 'Due for sign-off',
            blurb: 'Funds waiting, and the statement itself',
            needs: 'portfolio.attest',
          },
          {
            id: 'attest-history',
            label: 'What has been signed off',
            blurb: 'Every sign-off on record, and whether it still holds',
            // Readable by anyone who can read guidelines: it is the evidence,
            // and evidence only the person who signed can see is evidence
            // nobody checks.
            needs: 'guideline.read',
          },
        ],
      },
    ],
  },
  {
    // Menus of their own rather than groups under Settings (decision 27): the
    // funds and the groups that share limits are the firm's business structure,
    // not the appliance's configuration.
    title: 'Funds & groups',
    items: [
      {
        id: 'desks',
        label: 'Desks',
        blurb: 'The firm’s investment teams, their funds, groups and markets',
        needs: 'guideline.read',
      },
      {
        id: 'funds',
        label: 'View funds',
        blurb: 'Every fund you can see, its desk, and how many limits it carries',
        needs: 'guideline.read',
      },
      {
        id: 'funds-add',
        label: 'Add a fund',
        blurb: 'One at a time, or paste a list',
        needs: 'scope.create',
      },
      {
        id: 'groups',
        label: 'Groups',
        blurb: 'Sets of funds that share a set of limits',
        // Readable by anyone: a group's limits bind the funds they read (decision 84).
        needs: 'guideline.read',
      },
      {
        id: 'membership',
        label: 'View funds in a group',
        blurb: 'Put a fund into a group, and see what it inherits',
        needs: 'scope.membership',
      },
      {
        id: 'promotions',
        label: 'Limits repeated across funds',
        blurb: 'The same limit on several funds — promote it to a group',
        // For the people who can act on it (decision 29): an author proposes the
        // group rule and an approver decides it. It was once open to every
        // reader, when only an approver could propose and the author who noticed
        // the copies could not; that capability has since moved to the author.
        needs: 'scope.attach_rule',
      },
    ],
  },
  {
    // The language limits are written in. Authors and approvers use it daily,
    // which is the other reason it is not a setting.
    title: 'Dictionary',
    items: [
      {
        id: 'dictionary',
        label: 'Defined terms',
        blurb: 'What this system understands today',
        needs: 'dictionary.browse',
      },
      {
        id: 'dictionary-requests',
        label: 'Term requests',
        blurb: 'Words somebody has asked for',
        needs: 'dictionary.browse',
      },
      {
        id: 'dictionary-starter',
        label: 'Starter markets',
        blurb: 'Load the vocabulary a market normally uses',
        // Shown to those who can seed a market: an approver, because it is
        // vocabulary, and an administrator, because it is set-up.
        needsAny: ['dictionary.edit', 'appliance.operate'],
      },
      {
        id: 'dictionary-reconcile',
        label: 'Reconcile with holdings',
        blurb: 'Codes the holdings and the Dictionary disagree on',
        needs: 'dictionary.browse',
        pack: SNAPSHOT_EVALUATOR,
      },
    ],
  },
  {
    // What happened here: what the appliance did on its own, which everyone signed in
    // reads, and who did what. The audit trail is not a setting, and it is not with the
    // audit pack either: the pack is a disclosure assembled for somebody outside the
    // firm, and this is the supervisory read of the appliance itself.
    title: 'History',
    items: [
      {
        id: 'job-history',
        label: 'Scheduled Jobs',
        blurb: 'Every scheduled run, and what it produced',
      },
      {
        id: 'audit',
        label: 'Audit trail',
        blurb: 'Who did what, and when',
        needs: 'audit.trail.read',
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      {
        title: 'People',
        blurb: 'Accounts and what each can do',
        screens: [
          {
            id: 'users',
            label: 'Everyone',
            blurb: 'Every account, its role, and how it signs in',
            needs: 'user.manage',
          },
          {
            id: 'users-add',
            label: 'Add',
            blurb: 'Create an account and issue its invitation',
            needs: 'user.manage',
          },
        ],
      },
      {
        id: 'connector',
        label: 'Guidelines Mailbox Setup',
        blurb: 'Read guideline changes from a mailbox',
        needs: 'connector.configure',
      },
      {
        id: 'holdings-feed',
        label: 'SFTP - Auto loading snapshots',
        blurb: 'Collect holdings files from an SFTP server',
        needs: 'connector.configure',
        pack: SNAPSHOT_EVALUATOR,
      },
      {
        id: 'operations',
        label: 'System',
        blurb: 'Health, records and support',
        needs: 'appliance.operate',
      },
    ],
  },
]

/** Exported for the manual's completeness check. A screen added to the nav
 *  without a help topic gets a blank panel, which is how Audit trail shipped —
 *  and the index test could only ever check the other direction, that every
 *  topic is reachable. */
export const ALL_SCREENS: Screen[] = SECTIONS.flatMap((section) =>
  section.items.flatMap((item) => (isGroup(item) ? item.screens : [item])),
)

/** The screen each thing a job produced opens on. A client's document downloads instead. */
const OPENS_ON: Record<Exclude<JobProduct['type'], 'document'>, Tab> = {
  snapshot: 'holdings',
  run: 'verdicts',
  proposals: 'approvals',
  obligation: 'cure',
}

/** Two letters from a name, for the account control.
 *
 * First and last word, because a middle name is not what anybody recognises
 * themselves by — and a single-word name gives one letter rather than a
 * duplicated one. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0]![0]!
  const last = words.length > 1 ? words[words.length - 1]![0]! : ''
  return (first + last).toUpperCase()
}

/** What each role is called on screen, and the one line that says what it is
 *  for. The raw values are lower-case identifiers; a person should not have to
 *  read `approver` and infer authority over every limit in the firm. */
const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  author: 'Author',
  approver: 'Approver',
  admin: 'Administrator',
}

/**
 * What each role is for, in the words somebody would use for the job.
 *
 * One line each, and the card lists every role held rather than one of them.
 * It used to show `principal.role` — the most authoritative of the set, which
 * exists for the places a screen has room for one word — and this card has room
 * for all of them. On an appliance where the same person is the administrator
 * and the approver, that showed *Administrator · Deliberately cannot move a
 * limit* to somebody who was approving limits all day.
 *
 * `admin` no longer claims it. The old line was written from the role model's
 * point of view: *deliberately* answered a question nobody asked, and *move a
 * limit* is not a phrase anybody uses about a threshold. What an administrator
 * needs to know is what the job covers; what the *account* cannot do is a fact
 * about the capabilities held, which is decided below from the capabilities.
 */
const ROLE_BLURB: Record<string, string> = {
  viewer: 'Reads guidelines and their history.',
  author: 'Drafts limits, records documents and confirms reviews.',
  approver: 'Puts limits into force and declines proposals.',
  admin: 'Runs the system — accounts, funds, groups and the mailbox.',
}

/**
 * The one line that depends on the whole set rather than on any one role.
 *
 * Read off `capabilities`, which is the union of everything held, so it stays
 * true for somebody holding two roles — the case the old single-role blurb got
 * wrong.
 */
export function whatYouStillCannotDo(capabilities: string[]): string | null {
  if (capabilities.includes('rule.approve')) {
    // The control that survives every combination of roles, including holding
    // all four. `rules.approve` refuses it at the store, and meeting that as a
    // refusal after the work is worse than knowing it beforehand.
    return 'Whatever else you hold, you cannot approve a change you proposed yourself — somebody else has to look at it.'
  }
  if (capabilities.includes('rule.draft')) {
    return 'Nothing you draft takes effect until an approver agrees to it.'
  }
  return 'Nothing here changes a limit — drafting is an author’s job and putting one into force is an approver’s.'
}

/** Where a person lands on signing in: Verdicts where the Snapshot Evaluator runs and they read verdicts, Lookup otherwise. */
export function home(principal: Principal): Tab {
  return (principal.add_ons ?? []).includes(SNAPSHOT_EVALUATOR) && principal.capabilities.includes('evaluation.run')
    ? 'verdicts'
    : 'lookup'
}

export function App() {
  const [principal, setPrincipal] = useState<Principal | null>(null)
  const [checked, setChecked] = useState(false)
  const [tab, setTab] = useState<Tab>('lookup')
  /** A record to open on arriving at a screen, as Scheduled Jobs opens what a run produced. */
  const [focus, setFocus] = useState<{
    snapshot?: string
    run?: string
    obligation?: string
    /** Holdings whose verdicts Verdicts opens. */
    verdictsFor?: string
    /** The desk whose funds or groups Funds and Groups open on. */
    desk?: string
  } | null>(null)
  /** The part of System showing, chosen from Settings' list down the left. */
  const [systemSection, setSystemSection] = useState<SystemSection>('activity')
  // Signed out, there are two screens rather than one: an invitation has to be
  // redeemable by somebody who cannot sign in yet, which is every new account
  // and — on a fresh appliance — the first administrator.
  const [redeeming, setRedeeming] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const hint = useMenuHint()
  // A menu closing under the pointer fires no mouseleave, so its card goes with it.
  useEffect(() => {
    hint.hide()
  }, [openMenu])

  function choose(id: Tab) {
    setTab(id)
    setFocus(null)
    // Choosing a screen from the menus goes to it, including from the manual:
    // leaving the book open would change the back button's label and nothing else.
    setBook(false)
    setOpenMenu(null)
    setOpenGroup(null)
    setMenuOpen(false)
  }

  /** Go to what a job produced: the snapshot, the verdicts, the approvals queue, or the document. */
  function openProduct(product: JobProduct) {
    if (product.type === 'document') {
      void api.downloadSubmissionDocument(product.id)
      return
    }
    choose(OPENS_ON[product.type])
    if (product.type === 'snapshot') setFocus({ snapshot: product.id })
    if (product.type === 'run') setFocus({ run: product.id })
    if (product.type === 'obligation') setFocus({ obligation: product.id })
  }

  function canOpen(type: JobProduct['type']): boolean {
    if (type === 'document') return true
    const screen = ALL_SCREENS.find((s) => s.id === OPENS_ON[type])
    return Boolean(screen && (!screen.needs || principal?.capabilities.includes(screen.needs)))
  }
  // Help is per screen, and the panel follows whichever tab is showing — a
  // manual you have to find the right page of is a manual nobody opens.
  const [helpOpen, setHelpOpen] = useState(false)
  const [book, setBook] = useState(false)
  const accountRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (openMenu === null) return
    function dismiss(event: MouseEvent) {
      if (!(event.target as HTMLElement).closest('.menu')) {
        setOpenMenu(null)
        setOpenGroup(null)
      }
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpenMenu(null)
        setOpenGroup(null)
      }
    }
    document.addEventListener('mousedown', dismiss)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', dismiss)
      document.removeEventListener('keydown', key)
    }
  }, [openMenu])

  useEffect(() => {
    if (!menuOpen) return
    function dismiss(event: MouseEvent) {
      if (!accountRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', dismiss)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', dismiss)
      document.removeEventListener('keydown', key)
    }
  }, [menuOpen])

  // The session is a cookie this code cannot read, so "am I signed in" is a
  // question only the server can answer (§10.1). Asking it on load is also what
  // makes a deactivation take effect on the next request rather than whenever
  // the tab happens to be reloaded.
  useEffect(() => {
    api
      .me()
      .then((signedIn) => {
        setPrincipal(signedIn)
        setTab(home(signedIn))
      })
      .catch(() => setPrincipal(null))
      .finally(() => setChecked(true))
  }, [])

  if (!checked) return <p className="loading">Loading…</p>
  if (!principal) {
    return redeeming ? (
      <Redeem onDone={() => setRedeeming(false)} />
    ) : (
      <SignIn
        onSignedIn={(signedIn) => {
          setPrincipal(signedIn)
          setTab(home(signedIn))
        }}
        onRedeem={() => setRedeeming(true)}
      />
    )
  }

  const permitted = (screen: Screen) =>
    (!screen.needs || principal.capabilities.includes(screen.needs)) &&
    (!screen.needsAny || screen.needsAny.some((c) => principal.capabilities.includes(c))) &&
    (!screen.pack || (principal.add_ons ?? []).includes(screen.pack))

  const sections = SECTIONS.map((section) => {
    const items = section.items
      .map((item) =>
        isGroup(item) ? { ...item, screens: item.screens.filter(permitted) } : item,
      )
      .filter((item) => (isGroup(item) ? item.screens.length > 0 : permitted(item)))
    // A section left holding one group takes that group's name. An author sees
    // nothing under Settings but the dictionary, and calling that *Settings*
    // tells them their vocabulary is a setting — it is not, it is the language
    // their limits are written in.
    const only = items.length === 1 && isGroup(items[0]!) ? items[0]! : null
    return {
      key: section.title,
      title: only ? only.title : section.title,
      items: only ? only.screens : items,
    }
  }).filter((section) => section.items.length > 0)
  // The screen being shown, for the page label and for the help control. Falls
  // back to the first thing this person can actually open.
  const visibleScreens = sections.flatMap((section) =>
    section.items.flatMap((item) => (isGroup(item) ? item.screens : [item])),
  )
  const current =
    ALL_SCREENS.find((screen) => screen.id === tab && permitted(screen)) ??
    visibleScreens[0]

  // Settings lists its screens down the left, System's parts among them, and shows the
  // one chosen beside the list. Every entry sits under a heading: People, the
  // connections guidelines and holdings come in by, and System.
  const settingsItems = SECTIONS.find((section) => section.title === 'Settings')!
    .items.map((item) => (isGroup(item) ? { ...item, screens: item.screens.filter(permitted) } : item))
    .filter((item) => (isGroup(item) ? item.screens.length > 0 : permitted(item)))
  const inSettings = settingsItems.some((item) =>
    (isGroup(item) ? item.screens : [item]).some((screen) => screen.id === tab),
  )
  const systemScreen = settingsItems.find(
    (item): item is Screen => !isGroup(item) && item.id === 'operations',
  )
  const settingsGroups: SettingsGroup[] = []
  const connections: SettingsGroup = {
    title: 'Connections',
    hint: 'Where guidelines and holdings come in from',
    entries: [],
  }
  for (const item of settingsItems) {
    if (isGroup(item)) settingsGroups.push({ title: item.title, hint: item.blurb, entries: item.screens })
    else if (item !== systemScreen) connections.entries.push(item)
  }
  if (connections.entries.length > 0) settingsGroups.push(connections)
  const settingsHome = (settingsGroups[0]?.entries[0]?.id ?? systemScreen?.id) as Tab | undefined

  return (
    <DeskProvider firmWide={(principal.desks ?? []).length === 0}>
    <div className="app">
      {/* Two bars rather than one. The top carries identity — whose appliance
          this is, who you are signed in as, and the role that decides what the
          screens below will let you do. The second carries navigation. Crowding
          both into one row is what made the tabs read as a row of loose
          buttons. */}
      <header className="chrome">
        {/* Reads as one line: the product, then who makes it. The house mark is
            the larger of the two because it is artwork with its own wordmark
            inside it, and shrinking it to the height of a text label made it
            illegible. */}
        <span className="wordmark">
          <Wordmark />
          <span className="by">by</span>
          <BrandLogo className="chrome-logo" />
        </span>
        {/* Who you are and the way out, behind one control at the top right.
            The role stays *visible* inside it rather than being something you
            have to remember: every screen below behaves differently depending on
            it, and a person surprised by a refusal should be able to find out
            why in one click. */}
        <div className="account" ref={accountRef}>
          {/* Initials, name and role on the control itself. A bare glyph makes
              somebody click to find out who they are signed in as, and on a
              product where the answer decides what every screen will let them
              do, that is the wrong thing to hide. */}
          <button
            className="account-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="avatar" aria-hidden>
              {initials(principal.name)}
            </span>
            <span className="account-label">
              <span className="account-who">{principal.name}</span>
              <span className="account-role">{ROLE_LABELS[principal.role] ?? principal.role}</span>
            </span>
            <span className="chevron" aria-hidden />
          </button>
          {menuOpen && (
            <div className="account-menu" role="menu">
              <div className="account-card">
                <span className="avatar avatar-lg" aria-hidden>
                  {initials(principal.name)}
                </span>
                <div>
                  <p className="account-who">{principal.name}</p>
                  <p className="account-email">{principal.email}</p>
                </div>
              </div>
              {/* Every role held, not the most authoritative one. A person is
                  frequently more than one of these — in a small team the same
                  person is the author and the approver, and there is nobody else
                  to be either — and naming one of them describes an account
                  nobody has. */}
              {/* The roles by name, and what they mean on hover or focus. The
                  sentences used to sit open in the menu, which turned a menu of
                  two actions into a paragraph to read before reaching them. */}
              <p className="account-role-line" data-testid="account-roles" tabIndex={0}>
                {(principal.roles.length ? principal.roles : [principal.role])
                  .map((r) => ROLE_LABELS[r] ?? r)
                  .join(' · ')}
                <span className="role-hint-icon" aria-hidden>
                  i
                </span>
                <span className="role-detail" role="tooltip">
                  {(principal.roles.length ? principal.roles : [principal.role]).map((r) => (
                    <span className="clause" key={r}>
                      {ROLE_BLURB[r] ?? ''}
                    </span>
                  ))}
                  <span className="clause limitation">
                    {whatYouStillCannotDo(principal.capabilities)}
                  </span>
                </span>
              </p>
              <WorksOn />
              <div className="menu-divider" />
              <button
                role="menuitem"
                className="menu-item"
                onClick={() => {
                  setBook(true)
                  setHelpOpen(false)
                  setMenuOpen(false)
                }}
              >
                Help and documentation
              </button>
              <button
                role="menuitem"
                className="menu-item"
                onClick={() => {
                  void api.signOut().finally(() => setPrincipal(null))
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      <nav className="tabs" aria-label="Sections">
        {sections.map((section) => {
          if (section.key === 'Settings') {
            // No menu: Settings lists its screens down its own left frame, so the bar
            // goes straight there.
            return (
              <div className="menu section-menu" key={section.key}>
                <button
                  className="tab"
                  aria-current={inSettings ? 'page' : undefined}
                  onClick={() => settingsHome && choose(settingsHome)}
                >
                  {section.title}
                </button>
              </div>
            )
          }
          const screensIn = (item: Screen | Group) =>
            isGroup(item) ? item.screens : [item]
          const here = section.items.some((item) =>
            screensIn(item).some((screen) => screen.id === tab),
          )
          return (
            // `section-menu`: labels only, with each item's line of detail on
            // hover or focus. Two lines of text per entry made a menu read as a
            // page to be studied rather than a list to pick from.
            <div className="menu section-menu" key={section.title}>
              <button
                className="tab"
                aria-haspopup="menu"
                aria-expanded={openMenu === section.title}
                aria-current={here ? 'page' : undefined}
                onClick={() => {
                  setOpenGroup(null)
                  setOpenMenu((open) => (open === section.title ? null : section.title))
                }}
              >
                {section.title}
                <span className="chevron chevron-tab" aria-hidden />
              </button>
              {openMenu === section.title && (
                <div className="menu-panel" role="menu">
                  {section.items.map((item) =>
                    isGroup(item) ? (
                      <div
                        className="menu-group"
                        key={item.title}
                        onMouseEnter={() => setOpenGroup(item.title)}
                      >
                        <button
                          role="menuitem"
                          aria-haspopup="menu"
                          aria-expanded={openGroup === item.title}
                          className="menu-item"
                          onClick={() =>
                            setOpenGroup((open) => (open === item.title ? null : item.title))
                          }
                        >
                          <span className="menu-item-label">{item.title}</span>
                          <span className="chevron chevron-right" aria-hidden />
                        </button>
                        {openGroup === item.title && (
                          <div className="menu-flyout" role="menu" aria-label={item.title}>
                            {/* A group's detail heads the list it opens, which is
                                already the thing that appears on hover — a card
                                beside the row would sit under this very list. */}
                            <div className="menu-flyout-head">{item.blurb}</div>
                            {item.screens.map((screen) => (
                              <button
                                key={screen.id}
                                role="menuitem"
                                className="menu-item"
                                {...hint.bind(screen.blurb)}
                                aria-current={screen.id === tab ? 'page' : undefined}
                                onClick={() => choose(screen.id)}
                              >
                                <span className="menu-item-label">{screen.label}</span>
                                <span className="menu-item-blurb">{screen.blurb}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        key={item.id}
                        role="menuitem"
                        className="menu-item"
                        {...hint.bind(item.blurb)}
                        aria-current={item.id === tab ? 'page' : undefined}
                        onMouseEnter={(e) => {
                          setOpenGroup(null)
                          hint.bind(item.blurb).onMouseEnter(e)
                        }}
                        onClick={() => choose(item.id)}
                      >
                        <span className="menu-item-label">{item.label}</span>
                        <span className="menu-item-blurb">{item.blurb}</span>
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          )
        })}
        {/* At the right end, and outside the section list on purpose: the other
            menus answer *where do I go*, this one answers *what is waiting*. */}
        <PendingActions onGo={(screen) => choose(screen as Tab)} />
        {hint.card}
      </nav>
      <AddOnsContext.Provider value={principal.add_ons ?? []}>
      <main aria-label={current?.label} className={helpOpen ? 'with-help' : undefined}>
        {/* Beside the content rather than in the navigation: this control is
            about what is already on the screen. */}
        {!book && (
          <p className="help-link-row">
            <button
              className="help-link"
              aria-expanded={helpOpen}
              aria-label={`Help for ${current?.label ?? 'this screen'}`}
              onClick={() => setHelpOpen((open) => !open)}
            >
              <span className="help-badge" aria-hidden>
                ?
              </span>
              {helpOpen ? 'Close help' : `Help with ${current?.label ?? 'this screen'}`}
            </button>
          </p>
        )}
        {book ? (
          <>
            <button className="ghost" onClick={() => setBook(false)}>
              ← Back to {current?.label}
            </button>
            <HelpBook />
          </>
        ) : (
          <>
        {tab === 'lookup' && <Lookup canRetire={principal.capabilities.includes('rule.draft')} />}
        {tab === 'compare' && <Compare />}
        {tab === 'search' && <Search />}
        {tab === 'migration' && (
          <Migration
            canCreatePortfolios={principal.capabilities.includes('scope.create')}
            canAttachToScope={principal.capabilities.includes('scope.attach_rule')}
          />
        )}
        {tab === 'documents' && (
          <Documents canRegister={principal.capabilities.includes('document.register')} />
        )}
        {tab === 'clauses' && <ClauseReview />}
        {tab === 'approvals' && (
          <Approvals canApprove={principal.capabilities.includes('rule.approve')} me={principal.user_id} />
        )}
        {tab === 'reviews' && <Reviews mine />}
        {tab === 'reviews-all' && <Reviews mine={false} />}
        {tab === 'attest' && <Attest />}
        {tab === 'attest-history' && <AttestHistory />}
        {(tab === 'export' || tab === 'export-verify' || tab === 'export-pack') && (
          <Export
            section={
              tab === 'export-verify' ? 'verify' : tab === 'export-pack' ? 'pack' : 'rules'
            }
            canExportPack={principal.capabilities.includes('audit.pack.export')}
          />
        )}
        {(tab === 'dictionary' ||
          tab === 'dictionary-requests' ||
          tab === 'dictionary-starter') && (
          <Dictionary
            section={
              tab === 'dictionary-requests'
                ? 'requests'
                : tab === 'dictionary-starter'
                  ? 'starter'
                  : 'terms'
            }
            canEdit={principal.capabilities.includes('dictionary.edit')}
            // Either capability seeds a starter profile — see
            // `profiles.seed` for why the administrator is among them.
            canSeed={
              principal.capabilities.includes('dictionary.edit') ||
              principal.capabilities.includes('appliance.operate')
            }
          />
        )}
        {tab === 'membership' && <Membership firmWide={(principal.desks ?? []).length === 0} />}
        {(tab === 'submissions' ||
          tab === 'submissions-shapes' ||
          tab === 'submissions-senders') && (
          <Submissions
            canAuthorise={principal.capabilities.includes('connector.configure')}
            canMap={principal.capabilities.includes('layout_profile.manage')}
            section={
              tab === 'submissions-shapes'
                ? 'shapes'
                : tab === 'submissions-senders'
                  ? 'senders'
                  : 'refusals'
            }
          />
        )}
        {tab === 'funds' && (
          <Funds
            mode="list"
            firmWide={(principal.desks ?? []).length === 0}
            canManage={principal.capabilities.includes('scope.create')}
            desk={focus?.desk}
            onBack={() => choose('desks')}
          />
        )}
        {tab === 'funds-add' && <Funds mode="add" firmWide={(principal.desks ?? []).length === 0} />}
        {tab === 'desks' && (
          <Desks
            canManage={principal.capabilities.includes('desk.manage')}
            onView={(screen, deskId) => {
              choose(screen)
              setFocus({ desk: deskId })
            }}
          />
        )}
        {tab === 'groups' && (
          <Groups
            canCreate={principal.capabilities.includes('scope.create')}
            canDetach={principal.capabilities.includes('rule.draft')}
            firmWide={(principal.desks ?? []).length === 0}
            desk={focus?.desk}
            onBack={() => choose('desks')}
          />
        )}
        {tab === 'promotions' && (
          <Promotions
            canAttach={principal.capabilities.includes('scope.attach_rule')}
            firmWide={(principal.desks ?? []).length === 0}
          />
        )}
        {tab === 'verdicts' && (
          <Verdicts
            focusRun={focus?.run}
            focusSnapshot={focus?.verdictsFor}
            canReview={principal.capabilities.includes('verdict.review')}
          />
        )}
        {tab === 'whatif' && <WhatIf />}
        {tab === 'benchmarks' && (
          <Benchmarks canLoad={principal.capabilities.includes('holdings.load')} />
        )}
        {tab === 'agreement' && (
          <Agreement
            onReview={(run) => {
              choose('verdicts')
              setFocus({ run })
            }}
          />
        )}
        {tab === 'dictionary-reconcile' && (
          <Reconciliation
            canDismiss={principal.capabilities.includes('dictionary.edit')}
            canRequest={principal.capabilities.includes('rule.draft')}
          />
        )}
        {tab === 'cure' && (
          <CureObligations
            focus={focus?.obligation}
            canRecord={principal.capabilities.includes('cure.record')}
            canWaive={principal.capabilities.includes('cure.waive')}
          />
        )}
        {tab === 'calendars' && <SettlementCalendars />}
        {tab === 'excel' && <ExcelAddin />}
        {(tab === 'holdings' || tab === 'holdings-load') && (
          <Holdings
            mode={tab === 'holdings-load' ? 'load' : 'list'}
            onLoaded={() => choose('holdings')}
            focus={focus?.snapshot}
          />
        )}
        {tab === 'job-history' && (
          <JobHistory
            onOpen={openProduct}
            canOpen={canOpen}
            onOpenVerdicts={(snapshotId) => {
              choose('verdicts')
              setFocus({ verdictsFor: snapshotId })
            }}
            canRetryFiles={principal.capabilities.includes('connector.configure')}
            onMapLayout={
              principal.capabilities.includes('holdings.load') ? () => choose('holdings-load') : undefined
            }
          />
        )}
        {tab === 'audit' && <Audit />}
        {inSettings && (
          <div className="settings-layout">
            <SettingsNav
              groups={settingsGroups}
              system={systemScreen}
              current={tab}
              section={systemSection}
              onChoose={(id) => choose(id as Tab)}
              onSystem={(section) => {
                choose('operations')
                setSystemSection(section)
              }}
            />
            <div className="settings-content">
              {(tab === 'users' || tab === 'users-add') && (
                <Users
                  mode={tab === 'users-add' ? 'add' : 'list'}
                  onOwnRolesChanged={() => {
                    void api.me().then(setPrincipal).catch(() => {})
                  }}
                />
              )}
              {tab === 'connector' && (
                <MailboxSettings
                  senderAuthenticationOff={Boolean(principal.sender_authentication_relaxed)}
                />
              )}
              {tab === 'holdings-feed' && <HoldingsFeed />}
              {tab === 'operations' && (
                <Operations
                  section={systemSection}
                  relaxed={Boolean(principal.sender_authentication_relaxed)}
                  onJobHistory={() => choose('job-history')}
                  onApplianceChanged={() => {
                    void api.me().then(setPrincipal).catch(() => {})
                  }}
                />
              )}
            </div>
          </div>
        )}
          </>
        )}
      </main>
      {helpOpen && !book && (
        <HelpPanel id={tab} onClose={() => setHelpOpen(false)} />
      )}
      </AddOnsContext.Provider>
    </div>
    </DeskProvider>
  )
}

/**
 * Where the signed-in person works (decision 89): the whole firm, or their desks. The
 * screens show a fund's desk only to the whole firm; somebody on desks reads it here.
 */
function WorksOn() {
  const { firmWide, desks } = useDesks()
  return (
    <p className="account-desks" data-testid="account-desks" title="The funds you see and change">
      {firmWide
        ? 'Whole firm'
        : desks.length
          ? desks.map((d) => `${d.code} · ${d.name}`).join(', ')
          : ''}
    </p>
  )
}
